import { observer } from 'mobx-react';
import React from 'react';

// Config
import { config } from '../../config';

import './css.css';

// Translations
import translate from '../Translate.js';

// Components
import { FeatureFlag } from 'react-launch-darkly';
import * as icons from '../Icons/Icons';

// Proctor Type SIDs for modalities that require camera and audio recording
const VTC_SID = 'TS6d6011935f4646848ea4f7a29b4f61a5';
const RR_SID = 'TS9531c6b112f145018dae3cd2a9f60c4c';
const LIVE_SID = 'TS4b894875d26194988853e490dfb9bc41';
const AUTOMATED_SID = 'TS63dda27a6ba54c218449b934b7bb1ba8';

const AutomatedSettings = observer(class AutomatedSettings extends React.Component {
  constructor() {
    super();

    // Bind this to functions
    this.selectDefault = this.selectDefault.bind(this);
    this.selectPreset = this.selectPreset.bind(this);
    this.renderOption = this.renderOption.bind(this);

    // Set the initial state
    this.state = {
      options: [],
      preset: ''
    }
  }

  componentDidMount() {
    const { ExamStore } = this.props.store;
    const { categorySid, exam } = this.props;

    let { configuration } = ExamStore;

    if(!configuration) {
      configuration = exam.configuration;
    }

    // If this is a new exam, default to strict
    if(exam.sid) {
      ExamStore.getModalityPresets();
    } else {
      ExamStore.getModalityPresets(this.selectDefault);
    }

    if(categorySid) {
      ExamStore.getAutomatedOptions(categorySid);
    }

    const requiresRecording = categorySid === VTC_SID || categorySid === RR_SID || categorySid === LIVE_SID;

    // Pre-check any selected modality options
    if(
      configuration &&
      configuration.modalityOptions &&
      Object.keys(configuration.modalityOptions).length
    ) {
      const options = [];

      // Loops through all proctorTypes selected and applies the modality options to each proctor type
      Object.keys(configuration.modalityOptions).forEach(proctorTypeSid => {
        Object.keys(configuration.modalityOptions[proctorTypeSid]).forEach(option => {
          if(proctorTypeSid === categorySid) {
            options.push(option);
          }
        });
      });

      // Force recordWebcam and recordAudio for VTC and R&R
      if (requiresRecording) {
        if (options.indexOf('recordWebcam') === -1) {
          options.push('recordWebcam');
        }
        if (options.indexOf('recordAudio') === -1) {
          options.push('recordAudio');
        }
      }

      this.setState({
        options
      });
    } else if (requiresRecording) {
      // If no existing options but VTC or R&R is selected, ensure camera and audio are added
      this.setState({
        options: ['recordWebcam', 'recordAudio']
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { handleInput, categorySid } = this.props;
    const { options } = this.state;
    if(this.state.options !== prevState.options) {
      // these are the options that are required...
      const modalityOptions = {
        recordScreen: {}
      };

      options.forEach(option => {
        modalityOptions[option] = {}
      });

      handleInput('modalityOptions', null, true, null, null, false, modalityOptions, null, categorySid);
    }
  }

  selectDefault() {
    this.setState({
      preset: config.defaultAutomatedPreset
    }, () => {
      this.selectPreset();
    });
  }

  selectPreset() {
    const { ExamStore } = this.props.store;
    const presetSid = this.preset.value;
    const options = [];

    // Get the preset
    const callback = () => {
      if(ExamStore.modalityPreset && ExamStore.modalityPreset.optionGroups) {
        ExamStore.modalityPreset.optionGroups.forEach(optionGroup => {
          if(optionGroup.options) {
            optionGroup.options.forEach(option => {
              options.push(option.id);
            });
          }

          this.setState({
            options,
            preset: presetSid
          });
        })
      }
    }

    ExamStore.getModalityPreset(presetSid, callback);
  }

  toggleOption(option) {
    const { categorySid } = this.props;
    const options = [...this.state.options];
    const optionIndex = options.indexOf(option.id);

    const requiresRecording = categorySid === VTC_SID || categorySid === RR_SID || categorySid === LIVE_SID;
    const isAutomated = categorySid === AUTOMATED_SID;

    // Prevent toggling recordWebcam and recordAudio for VTC, R&R, and Live
    if (requiresRecording && (option.id === 'recordWebcam' || option.id === 'recordAudio')) {
      return; // Don't allow toggling these options
    }

    // For Automated Proctoring: Check if trying to disable camera when dependent features are enabled
    if (isAutomated && option.id === 'recordWebcam' && optionIndex > -1) {
      const dependentFeatures = [
        { id: 'verifyId', name: 'ID Verification' },
        { id: 'roomScan', name: 'Room Scan' }
      ].filter(feature => options.indexOf(feature.id) > -1);

      if (dependentFeatures.length > 0) {
        const featureNames = dependentFeatures.map(f => f.name).join(' and ');
        const featureIds = dependentFeatures.map(f => f.id);
        const buttonText = dependentFeatures.length > 1 ? 'Disable All' : 'Disable Both';
        
        const { SessionStore } = this.props.store;
        SessionStore.showConfirm(
          {
            heading: 'Camera Recording Required',
            body: `Disabling camera recording will also disable ${featureNames}.`,
            button: buttonText,
            cancel: 'Cancel'
          },
          () => this.handleDisableCameraWithDependencies(['recordWebcam', 'secondCamera', ...featureIds])
        );
        return;
      }
    }

    if(optionIndex === -1) {
      // Add the option if it's not selected
      options.push(option.id);
    } else {
      // Otherwise, remove it
      options.splice(optionIndex, 1);
    }

    // if recordWebcam is not selected, ensure that secondCamera is not selected
    if(option.id === 'recordWebcam' && options.indexOf('secondCamera') > -1) {
      options.splice(options.indexOf('secondCamera'), 1);
    }

    this.setState({
      options,
      preset: ''
    });
  }

  handleDisableCameraWithDependencies(optionsToRemove) {
    const options = this.state.options.filter(option => 
      !optionsToRemove.includes(option)
    );

    this.setState({
      options,
      preset: ''
    });

    // Close the dialog
    const { SessionStore } = this.props.store;
    SessionStore.closeDialog();
  }

  // Most of our feature flags use booleans but onboarding version
  // returns a string of 'new' or 'old'. Because of this we have to render this
  // feature flag slightly different than the others
  shouldRenderOption(version, option) {
    if (version === 'old') {
      return this.renderOption(option);
    } else {
      return null;
    }
  }

  renderOptionGroup(optionGroup) {
    return optionGroup.options.map((option) => {
      if(option.id === 'desktopAppCheck') {
        return (
          <FeatureFlag
            flagKey="smarter-services-utility-app"
            renderFeatureCallback={() => this.renderOption(option)}
          />
        )
      } else if (option.id === 'secondCamera') {
        return (
          <FeatureFlag
            flagKey="second-camera-modality-option"
            renderFeatureCallback={(version) => this.renderOption(option)}
          />
        )
      } else if (option.id === 'roomScan') {
        return (
          <FeatureFlag
            flagKey="onboarding-version"
            renderFeatureCallback={(version) => this.renderOption(option)}
          />
        )
      } else if (option.id === 'closeOpenTabs') {
        return (
          <FeatureFlag
            flagKey="onboarding-version"
            renderFeatureCallback={(version) => this.shouldRenderOption(version, option)}
          />
        )
      } else if (option.id === 'recordScreen') {
        return null;
      } else {
        return this.renderOption(option);
      }
    })
  }

  renderLockedOption(option, title, description, noticeMessage, isSelected = true) {
    return (
      <li
        key={option.id}
        className={`grid__cell block block--selectable${isSelected ? ' selected' : ''}`}
        style={{ cursor: 'not-allowed' }}
      >
        <div className="modality_option">
          <div className="modality_option__title">{title}</div>
          <div className="modality_option__description">
            {description}
            <br/><br/><strong className="important_notice">{noticeMessage}</strong>
          </div>
        </div>
        {isSelected ? (
          <i className="block__check">
            <icons.iconCheck />
            <span className="meta">selected</span>
          </i>
        ) : (
          <i className="block__check unchecked">
            &nbsp;
          </i>
        )}
        <span className="grid__select-focus"></span>
      </li>
    );
  }

  renderOption(option) {
    const { translation, categorySid } = this.props
    const { options } = this.state

    // If recordWebcam is not selected (in options), then don't render secondCamera
    if(option.id === 'secondCamera' && options.indexOf('recordWebcam') === -1) {
      return null;
    }

    // Check if current modality requires camera/audio recording
    const isVTC = categorySid === VTC_SID;
    const isRR = categorySid === RR_SID;
    const isLive = categorySid === LIVE_SID;
    const isAutomated = categorySid === AUTOMATED_SID;
    const requiresRecording = isVTC || isRR || isLive;

    // Handle recordWebcam (Camera Recording) for VTC and R&R
    if (option.id === 'recordWebcam' && requiresRecording) {
      const modalityName = isVTC ? 'Virtual Test Center' : isLive ? 'Live Proctoring' : 'Automated with Review';
      return this.renderLockedOption(
        option,
        option.title,
        option.description,
        `Camera recording is required for ${modalityName}.`
      );
    }

    // Handle recordAudio (Audio Recording) for VTC and R&R
    if (option.id === 'recordAudio' && requiresRecording) {
      const modalityName = isVTC ? 'Virtual Test Center' : isLive ? 'Live Proctoring' : 'Automated with Review';
      return this.renderLockedOption(
        option,
        option.title,
        option.description,
        `Audio recording is required for ${modalityName}.`
      );
    }

    // output this for live or vtc session and the option is roomScan
    if (option.id === 'roomScan' && [LIVE_SID, VTC_SID].includes(this.props.categorySid)) {
      return this.renderLockedOption(
        option,
        'Room Scan',
        'If required by your institution, a live proctor will ask the test taker to perform a room or desk scan before or during the exam. Your institution\'s exam settings determine the type of scan and any additional requirements.',
        'This setting cannot be changed at the exam level as it is an institution-wide setting.',
        true
      );
    }

    // output this for live or vtc session and the option is verifyId
    if (option.id === 'verifyId' && [LIVE_SID, VTC_SID, RR_SID].includes(this.props.categorySid)) {
      return this.renderLockedOption(
        option,
        'Verify ID',
        'If required by your institution, a live proctor will ask the test taker to present a photo ID for identity verification before or during the exam. Your institution\'s exam settings determine the type of scan and any additional requirements.',
        'This setting cannot be changed at the exam level as it is an institution-wide setting.',
        true
      );
    }

    // For Automated Proctoring: Disable ID verification when camera is not enabled
    if (option.id === 'verifyId' && isAutomated && options.indexOf('recordWebcam') === -1) {
      return this.renderLockedOption(
        option,
        option.title,
        option.description,
        'ID Verification requires camera recording to be enabled.',
        false
      );
    }

    // For Automated Proctoring: Disable Room Scan when camera is not enabled
    if (option.id === 'roomScan' && isAutomated && options.indexOf('recordWebcam') === -1) {
      return this.renderLockedOption(
        option,
        option.title,
        option.description,
        'Room Scan requires camera recording to be enabled.',
        false
      );
    }

    // this is the default output...
    
    return (
      <li
        key={option.id}
        className={`grid__cell block block--selectable${
          options.indexOf(option.id) > -1 ? ' selected' : ''
        }`}
      >

        <div className="modality_option">
          <div className="modality_option__title">{option.title}</div>
          <div className="modality_option__description">{option.description}</div>
        </div>

        {options.indexOf(option.id) > -1 ? (
          <i className="block__check">
            <icons.iconCheck />
            <span className="meta">selected</span>
          </i>
        ) : (
        <i className="block__check unchecked">
          &nbsp;
        </i>)}

        <button
          className="grid__select"
          type="button"
          onClick={() => this.toggleOption(option)}
        >
          {options.indexOf(option.id) > -1 &&
            translation.options.deselect.replace('%option%', option.title)
          }

          {options.indexOf(option.id) === -1 &&
            translation.options.select.replace('%option%', option.title)
          }
        </button>
        <span className="grid__select-focus"></span>
      </li>
    )
  }

  render() {
    const { categorySid, translation } = this.props;
    const { ExamStore } = this.props.store;
    const { automatedOptions, modalityPresets } = ExamStore;

    return (
      <div className="switch-list__settings text-block">
        <input
          className="inp-hidden"
          type="text"
          value={this.state.options.toString()}
          onChange={null}
          required
          aria-required="true"
          ref={(input) => this.emptyGuard = input}
        />

        {this.state.options.length === 0 &&
          <span className="note note--prominent note--error">
            {translation.error_msg}
          </span>
        }
  
        <span className="meta">
          {translation.heading}
        </span>

        <div className="modality_v3_notice">
          This modality will use the new V3 platform.
        </div>

        <div className="form-inline form-inline--alt">
          <label htmlFor={`${categorySid}-preset`}>
            {translation.presets.label}
          </label>

          <label className="select form-inline__main">
            <select
              id={`${categorySid}-preset`}
              onChange={this.selectPreset}
              ref={(input) => this.preset = input}
              value={this.state.preset}
            >
              <option value="">{translation.presets.custom}</option>

              {modalityPresets.map(
                (preset) =>
                <option key={preset.sid} value={preset.sid}>
                  {preset.title}
                </option>
              )}
            </select>
          </label>
        </div>
        
        {automatedOptions.map(
          (optionGroup) =>
            // If any groups need to be excluded....
            //optionGroup.title !== 'Recording Options' &&
              <section key={optionGroup.sid}>
                <strong className="grid-separator modality_option_group_title">
                  {optionGroup.title}
                </strong>

                <ul className="grid grid__modality_options">
                  {this.renderOptionGroup(optionGroup)}
                </ul>
              </section>
        )}
      </div>
    )
  }
})

export default translate('AutomatedSettings')(AutomatedSettings);



// WEBPACK FOOTER //
// ./src/app/components/ExamEdit/AutomatedSettingsV3.js